// Financial Marketplace launch switch (Platform Ops — owner-only kill-switch).
// A global master + per-country toggles. Everything ships OFF (dark); the
// KaziPeople superadmin flips a country on once its legal/regulatory clears.
// Reads are platform-staff; writes require platform_owner (enforced server-side).
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Switch, Space, Typography, Alert, Popconfirm, message, Tag, Divider } from 'antd';
import { PageHeader } from '../../components/console';
import { getLaunchStatus, setLaunch, type LaunchStatus } from '../../services/marketplaceLaunchService';

const { Text, Paragraph } = Typography;

const COUNTRY_LABEL: Record<string, string> = {
  TZ: '🇹🇿 Tanzania', KE: '🇰🇪 Kenya', UG: '🇺🇬 Uganda', RW: '🇷🇼 Rwanda',
};
const ORDER = ['TZ', 'KE', 'UG', 'RW'];

export const MarketplaceLaunchPage: React.FC = () => {
  const [status, setStatus] = useState<LaunchStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await getLaunchStatus());
    } catch {
      message.error('Could not load launch status');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const flip = async (code: string, enabled: boolean) => {
    setBusy(code);
    try {
      setStatus(await setLaunch(code, enabled));
      message.success(`${code === '*' ? 'Global master' : code} turned ${enabled ? 'ON' : 'OFF'}`);
    } catch (e: any) {
      message.error(e?.response?.status === 403 ? 'Only the KaziPeople superadmin can change this' : 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  const master = !!status?.master;

  // Turning something ON is going-live — confirmed via Popconfirm below. Turning OFF
  // is an immediate safety action (no confirm).
  const row = (code: string, label: React.ReactNode, enabled: boolean, disabled = false) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
      <span style={{ flex: 1 }}>{label}</span>
      <Tag color={enabled ? 'success' : 'default'}>{enabled ? 'LIVE' : 'OFF'}</Tag>
      {enabled ? (
        <Switch checked loading={busy === code} disabled={disabled} onChange={() => void flip(code, false)} />
      ) : (
        <Popconfirm
          title={`Turn ${code === '*' ? 'the global master' : COUNTRY_LABEL[code] || code} ON?`}
          description={code === '*'
            ? 'Enables the master gate. Countries you turn on then go live to real users.'
            : 'This country goes live to real users (subject to the global master + per-tenant entitlement). Confirm legal/regulatory has cleared.'}
          okText="Turn ON"
          okButtonProps={{ danger: true }}
          onConfirm={() => void flip(code, true)}
          disabled={disabled}
        >
          <Switch checked={false} loading={busy === code} disabled={disabled} />
        </Popconfirm>
      )}
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Marketplace launch"
        subtitle="Global + per-country kill-switch for the Financial Marketplace"
      />

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Superadmin only · audited"
        description="A country is live only when the global master AND that country are ON (and a tenant has the credit entitlement). Everything ships OFF. Only the KaziPeople superadmin can flip these; every change is recorded in the audit log."
      />

      <Card title="Global master" loading={loading} style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          The master gate. While OFF, the entire marketplace is dark everywhere regardless of per-country switches.
        </Paragraph>
        {status && row('*', <Text strong>Financial Marketplace master</Text>, master)}
      </Card>

      <Card title="Per-country" loading={loading}>
        <Paragraph type="secondary">Flip a country on once its legal/regulatory clears. Ineffective while the master is OFF.</Paragraph>
        <Divider style={{ margin: '8px 0' }} />
        {status && ORDER.map((c) => (
          <React.Fragment key={c}>
            {row(c, COUNTRY_LABEL[c] || c, !!status.countries[c], !master)}
          </React.Fragment>
        ))}
        {!master && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Country switches are disabled until the global master is ON.
          </Text>
        )}
      </Card>
    </div>
  );
};

export default MarketplaceLaunchPage;
